import { observableRef } from "mobx";
import { CostSourceSelect_BidContractLineItemFragment } from "src/components/autoPopulateSelects/CostSourceSelectField.generated";
import { DisplayNamedFragment } from "src/fragments/commonFragments.generated";
import { CostType, Maybe } from "src/generated/graphqlTypes";
import { Selectable } from "src/models/Selectable";
import { deriveFromInput } from "src/routes/projects/selections/recalc";
import {
  SpecsAndSelections_MaterialVariantFragment,
  SpecsAndSelections_ProjectItemFragment,
} from "src/routes/projects/selections/SpecsAndSelectionsPage.generated";
import { ObjectConfig } from "src/utils/formState";
import { makeSimpleAutoObservable } from "src/utils/makeSimpleAutoObservable";

/**
 * ObservableProjectItem is where the UX of the cost/margin/price interdependencies
 * are implemented in the various `update[Field]` methods on this class.
 */
export class ObservableProjectItem extends Selectable {
  fragment!: SpecsAndSelections_ProjectItemFragment;
  // These are the only fields from `fragment` that are editable
  costType!: CostType;
  unitOfMeasureId: string | undefined;
  locationId: string | undefined;
  quantity: Maybe<number>;
  markupAmount: number | null | undefined;
  markupPercent: number | null | undefined;
  unitCostInCents: number | null | undefined;
  totalCostInCents!: number;
  totalPriceInCents!: number;
  taskId!: string | undefined;
  bidItemId: string | undefined;
  bidItem: DisplayNamedFragment | undefined; // bidItem is updated on change
  bidContractLineItemId: string | undefined;
  bidContractLineItem: CostSourceSelect_BidContractLineItemFragment | undefined;
  materialVariant: SpecsAndSelections_MaterialVariantFragment | undefined;
  materialVariantId: string | undefined;

  constructor(projectItem: SpecsAndSelections_ProjectItemFragment) {
    super(projectItem.id, false);
    this.update(projectItem);
    makeSimpleAutoObservable(this, { fragment: observableRef });
  }

  update(projectItem: SpecsAndSelections_ProjectItemFragment) {
    this.id = projectItem.id;
    this.fragment = projectItem;
    this.costType = projectItem.costType;
    this.locationId = projectItem.location?.id;
    this.unitOfMeasureId = projectItem.unitOfMeasure?.id;
    this.quantity = projectItem.quantity;
    const { markupPercentage, totalMarkupInCents, unitCostInCents } = deriveFromInput({
      quantity: projectItem.quantity,
      totalCostInCents: projectItem.totalCostInCents,
      totalPriceInCents: projectItem.totalPriceInCents,
      markupBasisPoints:
        // Show contract markup when auto-calculated price as it can might have some markup difference
        // E.g. some rounded number will make the markup go up/down a few basis points (e.g. 10.00% vs 10.01%)
        this.disableMarkup && this.totalPriceInCents !== 0 ? this.fragment.projectMarkupBasisPoints : undefined,
    });
    this.markupAmount = totalMarkupInCents;
    this.markupPercent = markupPercentage;
    this.unitCostInCents = unitCostInCents;
    this.totalCostInCents = projectItem.totalCostInCents;
    this.totalPriceInCents = projectItem.totalPriceInCents;
    this.taskId = projectItem.task?.id;
    this.bidItemId = projectItem.bidItem?.id;
    this.bidItem = projectItem.bidItem ?? undefined;
    this.bidContractLineItemId = projectItem.bidContractLineItem?.id;
    this.bidContractLineItem = projectItem.bidContractLineItem ?? undefined;
    this.materialVariantId = projectItem.materialVariant?.id;
    this.materialVariant = projectItem.materialVariant ?? undefined;
  }

  get canEditCost(): boolean {
    return this.fragment.canEditCost.allowed && !this.bidContractLineItemId;
  }

  get canEditPrice(): boolean {
    return this.fragment.canEditScope.allowed;
  }

  get disableMarkup(): boolean {
    return this.fragment.project.canEditPrice.allowed === false;
  }
}

export type ProjectItemFormInput = {
  id: string;
  costType: Maybe<CostType>;
  locationId: Maybe<string>;
  unitOfMeasureId: Maybe<string>;
  quantity: Maybe<number>;
  markupAmount: Maybe<number>;
  markupPercent: Maybe<number>;
  unitCostInCents: Maybe<number>;
  totalCostInCents: Maybe<number>;
  totalPriceInCents: Maybe<number>;
  taskId: Maybe<string>;
  bidItemId: Maybe<string>;
  bidContractLineItemId: Maybe<string>;
  materialVariantId: Maybe<string>;
  // This isn't to the server, but putting it here for useFormStates.addRules to access
  canEditPrice: boolean;
};

// This form is auto-saved in SpecsAndSelectionsTable's autoSave callback that's
// passed to useFormStates in SpecsTable and SelectionsTable
export const projectItemFormConfig: ObjectConfig<ProjectItemFormInput> = {
  id: { type: "value" },
  costType: { type: "value" },
  locationId: { type: "value" },
  unitOfMeasureId: { type: "value" },
  quantity: { type: "value" },
  markupAmount: { type: "value" },
  markupPercent: { type: "value" },
  unitCostInCents: { type: "value" },
  totalCostInCents: { type: "value" },
  totalPriceInCents: { type: "value" },
  taskId: { type: "value" },
  bidItemId: { type: "value" },
  bidContractLineItemId: { type: "value" },
  materialVariantId: { type: "value" },
  canEditPrice: { type: "value", computed: true },
};
